<div role="main" class="settings-page-container" rvSpinner="billing-loader" [rvShowSpinner]="subscriptions.loadingItems || invoices.loadingItems">
  <div class="settings-page-body">
    <div class="settings-page-section">
      <div class="settings-page-section__header">
        <h2 class="tw-font-bold tw-text-[16px] tw-m-0">Billing Settings</h2>
      </div>
      <div *requireRole="'ua'" class="tw-px-3 sm:!tw-px-[24px] tw-pb-8">
        <div class="tw-flex tw-flex-wrap tw-items-start tw-gap-3 sm:!tw-items-center sm:!tw-gap-6">
          <span class="tw-font-medium tw-shrink-0">Billing Email:</span>
          <div class="tw-flex-1 tw-min-w-0">
            <span [shown]="company.billingContactEmails" class="tw-break-words">{{company.billingContactEmails?.join(', ')}}</span>
            <span [shown]="!company.billingContactEmails" class="tw-break-words">Not set. Please enter a billing email.</span>
          </div>
          <a mat-stroked-button uiSref="apps.settings.company-details" class="tw-shrink-0">Edit</a>
        </div>
      </div>
    </div>
    <div class="settings-page-section tw-mt-6">
      <div class="settings-page-section__header">
        <h2 class="tw-font-bold tw-text-[16px] tw-m-0">Subscriptions</h2>
      </div>
      <mat-divider class="light" />
      @if (subscriptions.items?.list.length
        && billingUtils.hasTaxExemptionExpired(subscriptions.items.list[0].customer)) {
        <div class="tw-pt-8 tw-px-3 sm:!tw-px-[24px]">
          <notification-alert
            title="Tax exemption expired"
            type="warning">
            <p>Please <a class="madero-link u_clickable" uiSref="apps.billing.subscription" [uiParams]="{subscriptionId: subscriptions.items.list[0].subscription.id}">click here</a> to update your tax exemption certificate.</p>
          </notification-alert>
        </div>
      }
      @if (subscriptions.apiError) {
        <div class="tw-pt-8 tw-px-3 sm:!tw-px-[24px]">
          <notification-alert
            id="subscriptions-error"
            type="danger"
            [title]="subscriptions.errorMessage"
            [message]="subscriptions.apiError">
          </notification-alert>
        </div>
      }
      <div class="scrollable-list horizontal-scroll tw-overflow-x-auto tw-w-full tw-px-3 sm:!tw-px-0 tw-min-h-[200px] tw-block" scrollingList (scrollEndEvent)="subscriptions.load()">
        <table mat-table id="subscriptionsListTable" [dataSource]="subscriptions.items.list" aria-label="Subscriptions" class="tw-min-w-[600px] tw-w-full tw-table-auto" style="display: table !important; height: auto !important;">
          <!-- Subscription Column -->
          <ng-container matColumnDef="subscription">
            <th mat-header-cell *matHeaderCellDef>Subscription</th>
            <td mat-cell *matCellDef="let item">
              <div class="tw-flex tw-items-center tw-gap-2">
                <div>
                  @if (showSubscriptionLink(item.subscription)) {
                    <a class="madero-link u_clickable" uiSref="apps.billing.subscription" [uiParams]="{subscriptionId: item.subscription.id}">{{item.subscription | subscriptionDescription}}</a>
                  }
                  @else {
                    <span>{{item.subscription | subscriptionDescription}}</span>
                  }
                </div>
                @if (item.subscription.has_scheduled_changes) {
                  <streamline-icon ariaLabel="More information" matTooltip="Pending Changes" matTooltipPosition="right" matTooltipClass="font-weight-bold" runZone name="info" width="14" height="14" class="aligner rise-blue pl-2"></streamline-icon>
                }
                @if (item.subscription.cancelled_at) {
                  <streamline-icon ariaLabel="More information" matTooltip="Pending Cancellation" matTooltipPosition="right" matTooltipClass="font-weight-bold" runZone name="info" width="14" height="14" class="aligner rise-blue pl-2"></streamline-icon>
                }
              </div>
            </td>
          </ng-container>

          <!-- Status Column -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let item">
              <status-name [subscription]="item.subscription"></status-name>
            </td>
          </ng-container>

          <!-- Renewal Date Column -->
          <ng-container matColumnDef="renewalDate">
            <th mat-header-cell *matHeaderCellDef>Renewal Date</th>
            <td mat-cell *matCellDef="let item">
              {{item.subscription.current_term_end * 1000 | date:'d-MMM-yyyy'}}
            </td>
          </ng-container>

          <!-- Plan Amount Column -->
          <ng-container matColumnDef="planAmount">
            <th mat-header-cell *matHeaderCellDef>Plan Amount</th>
            <td mat-cell *matCellDef="let item">
              {{item.subscription.plan_amount / 100 | currency}}
            </td>
          </ng-container>

          <!-- Ship To Column -->
          <ng-container matColumnDef="shipTo">
            <th mat-header-cell *matHeaderCellDef>Ship To</th>
            <td mat-cell *matCellDef="let item">
              @if (item.subscription.customer_id !== item.subscription.cf_ship_to_company) {
                <span>{{item.subscription.cf_ship_to_company_name || item.subscription.shipping_address?.company || item.subscription.cf_ship_to_company}}</span>
              }
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="subscriptionsDisplayedColumns; sticky: true" style="min-height: 48px !important; display: table-row !important;"></tr>
          <tr mat-row *matRowDef="let row; columns: subscriptionsDisplayedColumns" style="min-height: 48px !important; display: table-row !important;"></tr>

          @if (!currentPlanService.canModifyPlan) {
            <ng-container matColumnDef="canModifyPlan">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let item"></td>
              <td mat-footer-cell colspan="6" *matFooterCellDef class="tw-text-center">
                Subscription managed by {{currentPlanService.currentPlan.parentPlanCompanyName || "the Parent Company"}}
              </td>
            </ng-container>
            <tr mat-footer-row *matFooterRowDef="['canModifyPlan']; sticky: true" style="min-height: 48px !important; display: table-row !important;">
          }

          @if (subscriptions.items.list.length === 0 && currentPlanService.canModifyPlan && !subscriptions.loadingItems) {
            <ng-container matColumnDef="empty">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let item"></td>
              <td mat-footer-cell colspan="6" *matFooterCellDef class="tw-text-center">
                You haven't Subscribed to any Products yet.
              </td>
            </ng-container>
            <tr mat-footer-row *matFooterRowDef="['empty']; sticky: true" style="min-height: 48px !important; display: table-row !important;">
          }

        </table>
      </div>
    </div>
    <div class="settings-page-section tw-mt-6">
      <div class="settings-page-section__header tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3 sm:!tw-items-center">
        <h2 class="tw-font-bold tw-text-[16px] tw-m-0">Billing History</h2>
        @if(invoices.items.list.length > 0) {
          <a mat-stroked-button uiSref="apps.billing.unpaid" [uiParams]="{token: invoiceService.invoiceToken}" target="_blank" class="tw-shrink-0">View Unpaid Invoices</a>
        }
      </div>
      <mat-divider class="light" />
      @if (invoices.apiError) {
        <div class="tw-pt-8 tw-px-3 sm:!tw-px-[24px]">
          <notification-alert
            id="invoices-error"
            type="danger"
            [title]="invoices.errorMessage"
            [message]="invoices.apiError">
          </notification-alert>
        </div>
      }
      @if (invoiceService.apiError) {
        <div class="tw-pt-8 tw-px-3 sm:!tw-px-[24px]">
          <notification-alert
            id="invoice-service-error"
            type="danger"
            [title]="invoiceService.apiError">
          </notification-alert>
        </div>
      }
      <div class="scrollable-list horizontal-scroll tw-overflow-x-auto tw-w-full tw-px-3 sm:!tw-px-0 tw-min-h-[200px] tw-block" scrollingList (scrollEndEvent)="invoices.load()">
        <table mat-table id="invoicesListTable" [dataSource]="invoices.items.list" class="w-100 tw-min-w-[600px] tw-table-auto" aria-label="Billing History" style="display: table !important; height: auto !important;">
          <!-- Status Column -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let item">
              <mat-icon svgIcon="alert-triangle" class="unpaid" aria-label="Unpaid invoice" [shown]="item.invoice.status !== 'paid'" />
              <mat-icon svgIcon="check" class="paid" aria-label="Paid invoice" [shown]="item.invoice.status === 'paid'" />
            </td>
          </ng-container>

          <!-- Date Column -->
          <ng-container matColumnDef="date">
            <th mat-header-cell *matHeaderCellDef>Date</th>
            <td mat-cell *matCellDef="let item">{{item.invoice.date * 1000 | date:'MMM d, yyyy'}}</td>
          </ng-container>

          <!-- Description Column -->
          <ng-container matColumnDef="description">
            <th mat-header-cell *matHeaderCellDef>Description</th>
            <td mat-cell *matCellDef="let item" class="font-weight-bold">
              Invoice #{{item.invoice.id}}
            </td>
          </ng-container>

          <!-- Amount Column -->
          <ng-container matColumnDef="amount">
            <th mat-header-cell *matHeaderCellDef>Amount</th>
            <td mat-cell *matCellDef="let item">
              {{(item.invoice.amount_due || item.invoice.total) / 100 | currency}}
            </td>
          </ng-container>

          <!-- Invoice Column -->
          <ng-container matColumnDef="invoice">
            <th mat-header-cell *matHeaderCellDef>Invoice</th>
            <td mat-cell *matCellDef="let item">
              <button
                mat-icon-button
                aria-label="Download invoice"
                (click)="invoiceService.downloadInvoice(item.invoice.id)"
                (keyup.enter)="invoiceService.downloadInvoice(item.invoice.id)">
                <mat-icon class="download" svgIcon="download" />
              </button>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let item">
              @if (item.invoice.status !== 'paid') {
                <a class="btn btn-default btn-pay-now" uiSref="apps.billing.invoice" [uiParams]="{invoiceId: item.invoice.id, token: invoiceService.invoiceToken}" target="_blank">Pay Now</a>
              }
            </td>
          </ng-container>

          <!-- Ship To Column -->
          <ng-container matColumnDef="shipTo">
            <th mat-header-cell *matHeaderCellDef>Ship To</th>
            <td mat-cell *matCellDef="let item">
              @if (item.invoice.billing_address?.company !== item.invoice.shipping_address?.company) {
                <span>{{item.invoice.shipping_address?.company}}</span>
              }
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="invoicesDisplayedColumns; sticky: true" style="min-height: 48px !important; display: table-row !important;"></tr>
          <tr mat-row *matRowDef="let row; columns: invoicesDisplayedColumns" style="min-height: 48px !important; display: table-row !important;"></tr>

          @if (invoices.items.list.length === 0 && !invoices.loadingItems) {
            <ng-container matColumnDef="empty">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let item"></td>
              <td mat-footer-cell colspan="7" *matFooterCellDef class="tw-text-center">
                You do not have any invoices yet.
              </td>
            </ng-container>
            <tr mat-footer-row *matFooterRowDef="['empty']; sticky: true" style="min-height: 48px !important; display: table-row !important;">
          }

        </table>
      </div>
    </div>
    <div class="u_margin-lg text-center">
      <label>Need help with something?</label>
      <p><a class="madero-link" href="https://help.risevision.com/hc/en-us/articles/360041149991-Payment-instructions-and-common-questions-" target="_blank">Common Billing & Payment Questions</a></p>
    </div>
  </div>
</div>
